// Public unsubscribe tokens — the credential a logged-OUT recipient presents to
// `POST /unsubscribe` (arc 101 slice 07; per PERSON since #5700).
//
// Why a token at all: the digest footer used to link
// `…/unsubscribe?account=<githubLogin>`, which is (a) handled by nothing — the
// link 404'd, a CAN-SPAM exposure on every weekly send — and (b) an ENUMERABLE
// parameter: `github.com/<login>` is public, so anyone could have unsubscribed
// anyone. Signing the payload fixes both: the endpoint needs no session, and
// only a link WE minted is honoured.
//
// Shape: a compact HS256 JWS (primitives in `./jws`), payload
// `{ account, user, target, iat, exp }` — literally "HMAC over account + user +
// target + issued-at", with the expiry derived from `iat`.
//
//   - **It speaks for ONE person.** `user` is the recipient the mail landed on,
//     taken from the SIGNED payload and from nowhere else. The flip writes that
//     user's own preference rows, never the account's — the account-level
//     opt-out this module used to carry is the legacy shape below.
//   - **Scope is one target, never "all mail".** A link switches off exactly the
//     topic its mail carried — one `NotificationKind` — or `bundle` (one click
//     = the `only_urgent` preset). `escalation`, `platform_notice` and
//     `task_blocked` are NEVER targets: security and licence mail and an
//     ACTION NEEDED blocker are locked cells (`isLockedChannel`), and a
//     platform notice is not ours to mute. `assertUnsubscribeTarget`
//     refuses at the mint so a dead footer never ships.
//   - **No single-redeem, no `jti`.** Unsubscribing is idempotent (the store
//     upserts), so replay is harmless and a redemption ledger would only add a
//     way for the opt-out to fail.
//   - **TTL is generous on purpose.** CAN-SPAM requires the opt-out mechanism to
//     work for at least 30 days after the message was sent; 180 days beats that
//     with room for someone digging an old digest out of an archive, while
//     still bounding the life of a leaked link.
//
// OLD links (`{ account, category }`) keep verifying until
// `OLD_UNSUBSCRIBE_LINKS_HONOURED_UNTIL` — the ROUTE decides whether one still
// redeems — but this module only ever SIGNS the per-recipient shape: the
// deprecated legacy mint went with round 3 of #5700, so the only way an old
// link reaches an inbox now is one already sitting there. Verification returns
// a tagged result (never throws on hostile input) so the route can map each
// failure to the right status.

import { z } from 'zod';
import { NOTIFICATION_KINDS, NotificationKind } from '../entities/notification';
import { encodeHeader, encodeJson, openCompactJws, signSegments } from './jws';

// ─── the OLD category vocabulary — legacy, VERIFY-only (#5701 retires it) ────
//
// `UNSUBSCRIBABLE_CATEGORIES` and `UnsubscribableCategorySchema` exist for ONE
// reader: `LegacyUnsubscribeTokenPayloadSchema`, which keeps links already
// sitting in inboxes verifying until `OLD_UNSUBSCRIBE_LINKS_HONOURED_UNTIL`.
// The mint-side helpers that shared the vocabulary (`UnsubscribableCategory`,
// `isUnsubscribable`, `assertUnsubscribable`) went with the deprecated
// overload in round 3 of #5700 — nothing signs a category any more. The
// `email_unsubscribes` table and everything reading it survive until #5701.

/**
 * Categories a public one-click link may switch off — the OLD vocabulary,
 * legacy VERIFY-only until #5701 retires it.
 *
 * ONE reader remains: `LegacyUnsubscribeTokenPayloadSchema`, which keeps links
 * already sitting in inboxes verifying until `OLD_UNSUBSCRIBE_LINKS_HONOURED_UNTIL`.
 * No send path reads this list any more — nothing has minted a category link
 * since round 3 of #5700 (the mint-side helpers went with the deprecated
 * overload), and the fanout's footer links are per PERSON and carry a
 * notification kind (`UnsubscribeTarget`), never a category. The list is
 * therefore FROZEN: do NOT add a category here. A new value would widen only
 * what an OLD link may name — no mailable topic gains an unsubscribe link by
 * it — while reopening a vocabulary #5701 deletes with the
 * `email_unsubscribes` table. A new mailable topic gets an
 * `UnsubscribeTarget` on the per-recipient link instead, never a row here.
 */
export const UNSUBSCRIBABLE_CATEGORIES = [
  'digest.weekly',
  'ask_before_acting.*',
  'satisfying.*',
] as const;

export const UnsubscribableCategorySchema = z.enum(UNSUBSCRIBABLE_CATEGORIES);

// ─── unsubscribe targets (#5700) ─────────────────────────────────────────────

/**
 * The pseudo-target a bundle link switches off: everything the `only_urgent`
 * preset leaves on for the recipient's role. A distinct string on purpose — a
 * CATEGORY is never a target, and the two vocabularies never blur.
 */
export const UNSUBSCRIBE_BUNDLE_TARGET = 'bundle' as const;

/**
 * Kinds no public link may ever switch off. `escalation` is the locked
 * security/licence cell (`isLockedChannel`, the disclosure contract);
 * `platform_notice` is the platform's own voice; `task_blocked` ("ACTION NEEDED")
 * is an operational must-tell whose email is locked for every role (plan 101 03-10,
 * owner ruling 2026-10-08). A kind locked on email for every role belongs here —
 * `unsubscribe-token.test.ts` holds the two seams to that.
 */
const NEVER_UNSUBSCRIBABLE: ReadonlySet<NotificationKind> = new Set<NotificationKind>([
  NotificationKind.Escalation,
  NotificationKind.PlatformNotice,
  NotificationKind.TaskBlocked,
]);

/** The never-unsubscribable kinds, as a type. */
type NeverUnsubscribable =
  | typeof NotificationKind.Escalation
  | typeof NotificationKind.PlatformNotice
  | typeof NotificationKind.TaskBlocked;

/**
 * What a per-recipient link may switch off: every notification kind except the
 * never-unsubscribable three, plus the bundle. DERIVED off `NOTIFICATION_KINDS`,
 * so a new kind is a link target the moment it exists — unless it joins the
 * set above, which is a decision, not a default.
 */
export type UnsubscribeTarget =
  | Exclude<NotificationKind, NeverUnsubscribable>
  | typeof UNSUBSCRIBE_BUNDLE_TARGET;

/** Narrow an arbitrary string to a target a public link may switch off. */
export function isUnsubscribeTarget(target: string): target is UnsubscribeTarget {
  if (target === UNSUBSCRIBE_BUNDLE_TARGET) return true;
  return (
    NOTIFICATION_KINDS.includes(target as NotificationKind) &&
    !NEVER_UNSUBSCRIBABLE.has(target as NotificationKind)
  );
}

/**
 * The mint-side fence, old rule's shape: refuse to issue a link the redemption
 * side would refuse anyway. Throwing here surfaces the mistake at the send site
 * (a footer for security mail, a category spelled where a target belongs)
 * instead of shipping a dead "unsubscribe" button to a human.
 */
export function assertUnsubscribeTarget(target: string): asserts target is UnsubscribeTarget {
  if (isUnsubscribeTarget(target)) return;
  const never = NEVER_UNSUBSCRIBABLE.has(target as NotificationKind)
    ? ' (locked or platform mail)'
    : '';
  throw new Error(
    `[@developerz/domain] ${JSON.stringify(target)} is not an unsubscribe target${never}`,
  );
}

/**
 * Wire shape of the signed payload. `account` is the opaque account id (`acc_…`)
 * — not the `github_login` the old query param used, so the token leaks nothing
 * a public profile already tells you and can't be constructed by guessing.
 * `user` (`usr_…`) is the ONE person this link speaks for; `target` is the one
 * topic (or `bundle`) it switches off.
 *
 * `iat` / `exp` are unix seconds (NumericDate per RFC 7519).
 */
export const UnsubscribeTokenPayloadSchema = z.object({
  account: z.string().min(1).max(128),
  user: z.string().min(1).max(128),
  target: z.custom<UnsubscribeTarget>((v) => isUnsubscribeTarget(v as string)),
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive(),
});
export type UnsubscribeTokenPayload = z.infer<typeof UnsubscribeTokenPayloadSchema>;

/**
 * The OLD payload (arc 101 slice 07 … #5699): `{ account, category, iat, exp }`.
 * Kept readable so links already sitting in inboxes keep verifying until
 * `OLD_UNSUBSCRIBE_LINKS_HONOURED_UNTIL`; nothing new signs it — the legacy
 * mint went with round 3 of #5700.
 */
export const LegacyUnsubscribeTokenPayloadSchema = z.object({
  account: z.string().min(1).max(128),
  category: UnsubscribableCategorySchema,
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive(),
});
export type LegacyUnsubscribeTokenPayload = z.infer<typeof LegacyUnsubscribeTokenPayloadSchema>;

/**
 * The day an OLD (account, category) link stops REDEEMING. The route compares
 * its clock to this date; `verifyUnsubscribeToken` keeps opening old links
 * either way, so the cut-off is one decision at one seam.
 *
 * NOTHING HAS SHIPPED YET (written 2026-10-07, round 2 of #5700): no legacy
 * link sits in an inbox today beyond the digest's, which keeps minting them
 * until round 3. The date assumes #5700 ships by 2026-10-15; CAN-SPAM's 30-day
 * floor on that is 2026-11-14, rounded up to the 15th. RE-CHECK against the
 * REAL merge date before this goes out: it must be at least merge date + 30
 * days. #5701 drops the legacy schema with the `email_unsubscribes` table.
 */
export const OLD_UNSUBSCRIBE_LINKS_HONOURED_UNTIL = '2026-11-15T00:00:00.000Z';

/** 180 days — comfortably past CAN-SPAM's 30-day floor, still bounded. */
export const DEFAULT_UNSUBSCRIBE_TTL_SECONDS = 180 * 24 * 60 * 60;

export type SignUnsubscribeTokenInput = {
  /** Account id (`acc_…`) the mail was sent on behalf of. */
  account: string;
  /** The ONE person this link speaks for (`usr_…`). */
  user: string;
  /** The one topic this link switches off — or the bundle. */
  target: NotificationKind | string;
  /** Override the default TTL (seconds). */
  ttlSec?: number;
  /** Test seam — defaults to `Date.now`. */
  now?: () => number;
};

/**
 * Mint an unsubscribe token. Returns the compact JWS; the caller composes the
 * URL (`${apiOrigin}/unsubscribe?token=<jws>`).
 *
 * Throws on an empty secret or a target no link may switch off — both
 * programmer error at the send site, and a silently-dead unsubscribe link is
 * worse than a loud send failure.
 */
export function signUnsubscribeToken(input: SignUnsubscribeTokenInput, secret: string): string {
  if (!secret) {
    throw new Error('[@developerz/domain] signUnsubscribeToken: secret is empty');
  }

  const now = input.now ?? Date.now;
  const iat = Math.floor(now() / 1000);
  const exp = iat + (input.ttlSec ?? DEFAULT_UNSUBSCRIBE_TTL_SECONDS);

  assertUnsubscribeTarget(input.target);
  const payload: UnsubscribeTokenPayload = UnsubscribeTokenPayloadSchema.parse({
    account: input.account,
    user: input.user,
    target: input.target,
    iat,
    exp,
  });

  const headerB64 = encodeHeader();
  const payloadB64 = encodeJson(payload);
  return `${headerB64}.${payloadB64}.${signSegments(headerB64, payloadB64, secret)}`;
}

export type UnsubscribeVerifyFailure = 'malformed' | 'bad-signature' | 'bad-payload' | 'expired';

export type UnsubscribeVerifyResult =
  | { ok: true; payload: UnsubscribeTokenPayload }
  | { ok: true; payload: LegacyUnsubscribeTokenPayload; legacy: true }
  | { ok: false; reason: UnsubscribeVerifyFailure }
  /**
   * A link WE signed for `task_blocked`, minted before plan 101 03-10 locked that mail on.
   * Told apart from `bad-payload` so the route answers the clicker truthfully — this mail
   * cannot be switched off — not "invalid". ONLY `task_blocked`: `escalation` and
   * `platform_notice` were never mintable (`assertUnsubscribeTarget`), so no signed link
   * for them exists; a kind locked later widens this deliberately, with its own words.
   */
  | { ok: false; reason: 'locked-target'; target: typeof NotificationKind.TaskBlocked };

/** A per-recipient payload, well-formed except that its target is the since-locked kind. */
const LockedTargetPayloadSchema = UnsubscribeTokenPayloadSchema.extend({
  target: z.literal(NotificationKind.TaskBlocked),
});

/**
 * Verify an unsubscribe token. Never throws on hostile input; returns a tagged
 * result so the route maps `malformed`/`bad-signature` → 400, `bad-payload` →
 * 400, `expired` → 410, and `locked-target` to the page saying the mail cannot be
 * switched off (checked before expiry: that answer stays true however old the link).
 *
 * Both generations verify: the new payload first, then the legacy one (a legacy
 * token has no `user`/`target`, a new one no `category`, so exactly one schema
 * ever fits). A legacy hit carries `legacy: true` — the ROUTE then applies
 * `OLD_UNSUBSCRIBE_LINKS_HONOURED_UNTIL` — while `bad-payload` still covers a
 * token signed for a category or target we no longer honour, without needing a
 * revocation list.
 */
export function verifyUnsubscribeToken(
  token: string,
  secret: string,
  opts: { now?: () => number } = {},
): UnsubscribeVerifyResult {
  if (!secret) {
    throw new Error('[@developerz/domain] verifyUnsubscribeToken: secret is empty');
  }
  const opened = openCompactJws(token, secret);
  if (!opened.ok) return { ok: false, reason: opened.reason };

  const now = opts.now ?? Date.now;
  const parsed = UnsubscribeTokenPayloadSchema.safeParse(opened.payload);
  if (parsed.success) {
    if (parsed.data.exp * 1000 <= now()) return { ok: false, reason: 'expired' };
    return { ok: true, payload: parsed.data };
  }

  const locked = LockedTargetPayloadSchema.safeParse(opened.payload);
  if (locked.success) return { ok: false, reason: 'locked-target', target: locked.data.target };

  const legacy = LegacyUnsubscribeTokenPayloadSchema.safeParse(opened.payload);
  if (!legacy.success) return { ok: false, reason: 'bad-payload' };
  if (legacy.data.exp * 1000 <= now()) return { ok: false, reason: 'expired' };
  return { ok: true, payload: legacy.data, legacy: true };
}
